import React from 'react';
import { useTranslation } from '../hooks/useTranslation';

const Contacts: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section */}
      <section 
        className="relative pt-16 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(https://sonicmakers-uploads.lon1.digitaloceanspaces.com/public/backgrounds/contacts-top.gif)',
          backgroundPosition: 'center',
          backgroundSize: 'cover'
        }}
      >
        <div className="container mx-auto px-4 text-center flex flex-col justify-center min-h-[70vh]" style={{ paddingTop: '80px', paddingBottom: '120px' }}>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 animate-fade-in">
            {t('contacts.hero_title')}
            <span className="block bg-gradient-to-r from-purple-400 to-pink-600 bg-clip-text text-transparent leading-tight py-2">
              SONIC MAKERS
            </span>
          </h1>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto animate-fade-in">
            {t('contacts.hero_subtitle')}
          </p>
        </div>
      </section>

      {/* Contact Information Section */}
      <section className="py-20 bg-gray-900/50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              
              {/* General Information */}
              <div className="p-8 bg-gray-800/50 rounded-lg backdrop-blur-sm">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 7.89a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white">
                    {t('contacts.general_info_title')}
                  </h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-gray-400 text-sm mb-1">{t('contacts.email_label')}</p>
                    <a 
                      href="mailto:hello@sonicmakers.com" 
                      className="text-purple-400 hover:text-purple-300 transition-colors"
                    >
                      {t('contacts.general_email')}
                    </a>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">{t('contacts.response_time')}</p>
                    <p className="text-gray-300">{t('contacts.general_response_time')}</p>
                  </div>
                </div>
              </div>

              {/* Technical Support */}
              <div className="p-8 bg-gray-800/50 rounded-lg backdrop-blur-sm">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white">
                    {t('contacts.support_title')}
                  </h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-gray-400 text-sm mb-1">{t('contacts.email_label')}</p>
                    <a 
                      href="mailto:support@sonicmakers.com" 
                      className="text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      {t('contacts.support_email')}
                    </a>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">{t('contacts.response_time')}</p>
                    <p className="text-gray-300">{t('contacts.support_response_time')}</p>
                  </div>
                </div>
              </div>

              {/* Business Inquiries */}
              <div className="p-8 bg-gray-800/50 rounded-lg backdrop-blur-sm">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white">
                    {t('contacts.business_title')}
                  </h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-gray-400 text-sm mb-1">{t('contacts.email_label')}</p>
                    <a 
                      href="mailto:business@sonicmakers.com" 
                      className="text-green-400 hover:text-green-300 transition-colors"
                    >
                      {t('contacts.business_email')}
                    </a>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">{t('contacts.response_time')}</p>
                    <p className="text-gray-300">{t('contacts.business_response_time')}</p>
                  </div>
                </div>
              </div>

            </div>


          </div>
        </div>
              </section>

      {/* Location Section */}
      <section 
        className="py-20 bg-cover bg-center bg-no-repeat relative"
        style={{
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(https://sonicmakers-uploads.lon1.digitaloceanspaces.com/public/backgrounds/contacts-bottom.gif)',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-600 rounded-full mb-6">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h2 className="text-4xl font-bold mb-6 text-white">
              {t('contacts.location_title')}
            </h2>
            <p className="text-xl text-purple-400 mb-8 font-medium">
              {t('contacts.location_text')}
            </p>
            
                         {/* Address Block */}
             <div className="flex justify-center mb-8">
               <div className="p-8 bg-gray-800/50 rounded-lg backdrop-blur-sm border border-purple-500/20 w-full max-w-sm">
                                                  <div className="flex items-center mb-4">
                   <div className="w-10 h-10 bg-gray-500 rounded-lg flex items-center justify-center mr-3">
                     <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                     </svg>
                   </div>
                   <h3 className="text-lg font-semibold text-white">
                     {t('contacts.head_office_address')}
                   </h3>
                 </div>
                 <div className="space-y-1 text-left" style={{ marginLeft: '3.25rem' }}>
                   <div className="font-medium" style={{ color: '#737373' }}>3rd Floor</div>
                   <div className="font-medium" style={{ color: '#737373' }}>86-90 Paul Street</div>
                   <div className="font-medium" style={{ color: '#737373' }}>London</div>
                   <div className="font-medium" style={{ color: '#737373' }}>EC2A 4NE</div>
                   <div className="font-medium" style={{ color: '#737373' }}>United Kingdom</div>
                 </div>
              </div>
            </div>
            
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              {t('contacts.location_description')}
            </p>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Contacts;